// src/plugins/autotrack/index.ts

// Export types
export * from './types';

// Export utilities
export { AutoTrackUtils } from './utils';

import { IStandalonePlugin } from '../../types/plugin';
import { PluginContext } from '../../types/plugin_context';
import { PluginType } from '../../types/plugin_categories';
import { IUniqodeInsightsClient } from '../../types/sdk';
import { AutoTrackUtils } from './utils';
import { AutoTrackConfig } from './types';
import { PageViewModule } from './modules/PageViewModule';
import { ClickModule } from './modules/ClickModule';
import { FormModule } from './modules/FormModule';

export class AutoTrackPlugin implements IStandalonePlugin {
  public readonly name = 'AutoTrackPlugin';
  public readonly pluginType = PluginType.STANDALONE;
  public readonly version = '2.0.0';

  private config: AutoTrackConfig;
  private client?: IUniqodeInsightsClient;
  private isStarted = false;
  private context?: PluginContext;

  private pageViewModule?: PageViewModule;
  private clickModule?: ClickModule;
  private formModule?: FormModule;

  public initialize(context: PluginContext): void {
    this.client = context.client;
    this.context = context;

    this.config = {
      pageViews: true,
      clicks: true,
      forms: true,
      scrolling: true,
      ...context.pluginConfig,
    };

    // Normalize boolean configs to objects
    if (typeof this.config.pageViews === 'boolean') {
      this.config.pageViews = { enabled: this.config.pageViews };
    }
    if (typeof this.config.clicks === 'boolean') {
      this.config.clicks = { enabled: this.config.clicks };
    }
    if (typeof this.config.forms === 'boolean') {
      this.config.forms = { enabled: this.config.forms };
    }
    if (typeof this.config.scrolling === 'boolean') {
      this.config.scrolling = { enabled: this.config.scrolling };
    }

    if (typeof this.config.pageViews === 'boolean') {
      this.config.pageViews = { enabled: this.config.pageViews };
    }
    if (typeof this.config.clicks === 'boolean') {
      this.config.clicks = { enabled: this.config.clicks };
    }
    if (typeof this.config.forms === 'boolean') {
      this.config.forms = { enabled: this.config.forms };
    }
    if (typeof this.config.scrolling === 'boolean') {
      this.config.scrolling = { enabled: this.config.scrolling };
    }

    if (this.config.respectDoNotTrack && AutoTrackUtils.respectsDoNotTrack()) {
      this.context?.logger.debug('Do Not Track detected, skipping initialization');
      return;
    }

    this.initializeModules();
    this.context?.logger.debug('Initialized with config:', this.config);
  }

  public start(): void {
    if (this.isStarted ?? !this.client) {
      return;
    }

    // Check Do Not Track preference again
    if (this.config.respectDoNotTrack && AutoTrackUtils.respectsDoNotTrack()) {
      this.context?.logger.debug('AutoTrackPlugin: Do Not Track detected, not starting');
      return;
    }

    this.isStarted = true;
    this.startModules();

    this.context?.logger.debug('AutoTrackPlugin: Started automatic tracking');
  }

  public stop(): void {
    if (!this.isStarted) {
      return;
    }

    this.isStarted = false;
    this.stopModules();

    this.context?.logger.debug('AutoTrackPlugin: Stopped automatic tracking');
  }

  public destroy(): void {
    this.stop();
    this.destroyModules();

    this.context?.logger.debug('AutoTrackPlugin: Destroyed');
  }

  private initializeModules(): void {
    if (!this.client) return;

    if (this.config.pageViews && typeof this.config.pageViews === 'object') {
      this.pageViewModule = new PageViewModule(this.config.pageViews);
      this.pageViewModule.initialize(this.client, this.context?.logger);
      console.debug('AutoTrackPlugin: PageViewModule initialized');
    }

    if (this.config.clicks && typeof this.config.clicks === 'object') {
      this.clickModule = new ClickModule(
        this.config.clicks,
        this.config.excludeSelector,
        this.config.includeSelector
      );
      this.clickModule.initialize(this.client, this.context?.logger);
    }

    if (this.config.forms && typeof this.config.forms === 'object') {
      this.formModule = new FormModule(
        this.config.forms,
        this.config.excludeSelector,
        this.config.includeSelector
      );
      this.formModule.initialize(this.client, this.context?.logger);
    }
  }

  private startModules(): void {
    this.pageViewModule?.start();
    this.clickModule?.start();
    this.formModule?.start();
  }

  private stopModules(): void {
    this.pageViewModule?.stop();
    this.clickModule?.stop();
    this.formModule?.stop();
  }

  private destroyModules(): void {
    this.pageViewModule?.destroy();
    this.clickModule?.destroy();
    this.formModule?.destroy();

    this.pageViewModule = undefined;
    this.clickModule = undefined;
    this.formModule = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private log(message: string, data?: any): void {
    if (this.config.debugMode) {
      if (data) {
        this.context?.logger.debug(message, data);
      } else {
        this.context?.logger.debug(message);
      }
    }
  }

  /**
   * Get current tracking statistics from all modules
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getTrackingStats(): Record<string, any> {
    return {
      isActive: this.isStarted,
      modules: {
        pageView: this.pageViewModule?.isModuleActive() ?? false,
        click: this.clickModule?.isModuleActive() ?? false,
        form: this.formModule?.isModuleActive() ?? false,
      },
      stats: {
        pageView: this.pageViewModule?.getPageSessionData?.() ?? {},
        click: this.clickModule?.getRageClickStats?.() ?? {},
        form: this.formModule?.getFormStats?.() ?? {},
      },
    };
  }

  /**
   * Enable/disable specific tracking modules at runtime
   */
  public configureModule(
    moduleName: 'pageViews' | 'clicks' | 'scrolling' | 'forms',
    enabled: boolean
  ): void {
    switch (moduleName) {
      case 'pageViews':
        if (enabled && !this.pageViewModule?.isModuleActive()) {
          this.pageViewModule?.start();
        } else if (!enabled && this.pageViewModule?.isModuleActive()) {
          this.pageViewModule?.stop();
        }
        break;

      case 'clicks':
        if (enabled && !this.clickModule?.isModuleActive()) {
          this.clickModule?.start();
        } else if (!enabled && this.clickModule?.isModuleActive()) {
          this.clickModule?.stop();
        }
        break;

      case 'forms':
        if (enabled && !this.formModule?.isModuleActive()) {
          this.formModule?.start();
        } else if (!enabled && this.formModule?.isModuleActive()) {
          this.formModule?.stop();
        }
        break;
    }

    this.context?.logger.debug(`Module ${moduleName} ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Manual tracking methods for advanced use cases
   */
  public manualTrack = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pageView: (data?: Record<string, any>) => {
      this.pageViewModule?.trackManualPageView?.(data ?? {});
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    click: (element: Element, data?: Record<string, any>) => {
      this.clickModule?.trackManualClick?.(element, data ?? {});
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formSubmit: (data: Record<string, any>) => {
      this.formModule?.trackManualFormSubmit?.(data);
    },
  };
}
